import { EditType } from "@components/EditableCell"
import { Row } from "@components/VerticalTable"
import NodeTable from "@features/graph/details/components/TableNodeDetails/NodeTable"
import { GraphEditMode } from "@features/graph/graphViewModel"
import { graphReplaceNode } from "@features/graph/state/graphReplaceNode"
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material"
import { BaseGraphTableNode, GraphNodeData } from "@store/services/converter/types"
import { SyntheticEvent, useEffect, useState } from "react"
import { Node, useStoreApi } from "reactflow"
import { findNode } from "utils/graph"
import { ParentChildDetails } from "@features/graph/details/components/ParentChildDetails/ParentChildDetails"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useLocalStorage } from "usehooks-ts"
import { NodeRowsAccordion } from "@features/graph/details/components/NodeRowsAccordion"
import { NodeDetailsLabel } from "@features/graph/details/components/NodeDetailsLabel"
import NodeTagsAccordion from "@features/graph/details/components/NodeTagsAccordion"

interface TableNodeDetailsProps {
  nodeId: string
  editMode: GraphEditMode
  graphKey?: string
}

export const TableNodeDetails = ({ nodeId, graphKey, editMode }: TableNodeDetailsProps) => {
  const graphStore = useStoreApi()
  const [node, setNode] = useState<Node<GraphNodeData>>()
  const [expandedPropertiesTable, setExpandedPropertiesTable] = useLocalStorage(
    `nodePropertiesTableAccordionExpanded`,
    true,
  )

  useEffect(() => {
    setNode(findNode(graphStore, nodeId))
  }, [graphStore, nodeId])

  if (!node) {
    return
  }

  const properties = node.data.node?.properties ?? {}
  const sampleValue = properties.a_sample_value

  const { Description, label } = node.data?.display ?? {}

  const handleSampleValueFieldBlur = async (field: "a_sample_value", newValue: string) => {
    if (!graphKey) {
      return
    }

    node.data.node.properties[field] = Number(newValue)
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(structuredClone(node))
  }

  const handleTableCellBlur = async (
    field: keyof BaseGraphTableNode,
    rowIndex: number,
    cellIndex: number,
    newValue: string,
  ) => {
    if (!graphKey) return

    if (field === "index") {
      // @ts-expect-error Type of node.data.table!["index"][rowIndex] and newValue needs to be hand tested. Is it really a string or it is an number?
      node.data.table!["index"][rowIndex] = Number(newValue)
    } else if (field === "data") {
      // @ts-expect-error Type of node.data.table!["index"][rowIndex] and newValue needs to be hand tested. Is it really a string or it is an number?
      node.data.table![field][rowIndex][cellIndex] = Number(newValue)
    }

    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(structuredClone(node))
  }

  const nodeRows: Row[] = [
    { label: "Type", value: "Table" },
    // { label: "Color", children: <ColorPreview color={color} />, value: color },
    { label: "Description", value: Description || "-" },
    { label: "Unit", value: formatValuePythonic("", node.data?.display["Unit Format"]) || "-" },
    {
      label: "Exclude From Export",
      value: node.data?.display.exportExcluded ? "yes" : "no",
    },
    { label: "Link", value: node.data.display.Link ?? "" },
  ]

  // 0 is valid value
  if (sampleValue !== null && sampleValue !== undefined) {
    nodeRows.push({
      label: "Sample value",
      value: sampleValue,
      editable: editMode === GraphEditMode.SCENARIO,
      type: EditType.Number,
      onBlur: (newValue) => handleSampleValueFieldBlur("a_sample_value", newValue),
    })
  }

  return (
    <Grid container direction="column" sx={{ p: 1 }} spacing={1}>
      <Grid item>
        <NodeDetailsLabel label={label} />
      </Grid>
      <NodeRowsAccordion nodeRows={nodeRows} />
      <NodeTagsAccordion tags={node.data.display.tags ?? []} />
      <Grid item sx={{ width: expandedPropertiesTable ? "auto" : "100%" }}>
        <Accordion
          expanded={expandedPropertiesTable}
          onChange={(_event: SyntheticEvent, isExpanded: boolean) => {
            setExpandedPropertiesTable(isExpanded)
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>Properties Table</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <NodeTable
              onBlur={handleTableCellBlur}
              editable={editMode === GraphEditMode.SCENARIO}
              table={node.data.table!}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <ParentChildDetails />
    </Grid>
  )
}
